import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  canRenderAdsenseOnCurrentRuntime,
  ensureAdsenseScript,
  normalizeAdsensePublisherId,
  pushAdsenseRender,
} from '../../utils/google-ads';

const AdSenseUnit = ({
  publisherId,
  slotId,
  format = 'auto',
  className = '',
  style = {},
  label = 'Publicidade',
  variant = 'strip',
  layoutKey = '',
}) => {
  const [isUnfilled, setIsUnfilled] = useState(false);
  const adElementRef = useRef(null);
  const feedCardMinHeight = useMemo(() => {
    if (variant !== 'feed-card') return 320;
    const explicitMinHeight = Number(style?.minHeight);
    if (Number.isFinite(explicitMinHeight) && explicitMinHeight > 0) {
      return explicitMinHeight;
    }
    return 320;
  }, [style?.minHeight, variant]);
  const normalizedPublisherId = useMemo(
    () => normalizeAdsensePublisherId(publisherId),
    [publisherId]
  );

  useEffect(() => {
    setIsUnfilled(false);
    if (!normalizedPublisherId || !slotId) return undefined;
    if (!ensureAdsenseScript(normalizedPublisherId)) return undefined;

    const timer = window.setTimeout(() => {
      pushAdsenseRender();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [normalizedPublisherId, slotId]);

  useEffect(() => {
    if (!normalizedPublisherId || !slotId) return undefined;

    const timer = window.setTimeout(() => {
      const element = adElementRef.current;
      const hasRenderedFrame = Boolean(element?.querySelector('iframe'));
      if (
        element?.getAttribute('data-ad-status') === 'unfilled' ||
        (!hasRenderedFrame && element?.innerHTML.trim() === '')
      ) {
        setIsUnfilled(true);
      }
    }, 5000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [normalizedPublisherId, slotId]);

  if (
    !normalizedPublisherId ||
    !slotId ||
    isUnfilled ||
    !canRenderAdsenseOnCurrentRuntime()
  ) {
    return null;
  }

  if (variant === 'feed-card') {
    return (
      <article
        className={`mb-4 border-b border-on-surface/30 bg-surface px-3 py-3 text-on-surface sm:py-4 ${className}`.trim()}
        data-post-no-open
      >
        <div className="flex gap-3">
          <div className="h-10 w-10 shrink-0 rounded-full bg-on-surface/10" />
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-semibold text-on-surface">Patrocinado</span>
              <span className="text-sm text-on-surface/50">@publicidade</span>
              <span className="text-sm text-on-surface/40">·</span>
              <span className="text-sm text-on-surface/50">{label}</span>
            </div>
            <div
              className="overflow-hidden rounded-2xl border border-on-surface/10 bg-background"
              style={{ minHeight: feedCardMinHeight }}
            >
              <ins
                ref={adElementRef}
                className="adsbygoogle"
                style={{
                  display: 'block',
                  minHeight: feedCardMinHeight,
                  width: '100%',
                  ...style,
                }}
                data-ad-client={normalizedPublisherId}
                data-ad-slot={slotId}
                data-ad-format={layoutKey ? 'fluid' : format}
                data-ad-layout-key={layoutKey || undefined}
                data-full-width-responsive={layoutKey ? undefined : 'true'}
              />
            </div>
            <div className="mt-3 flex max-w-sm justify-between text-xs font-medium text-on-surface/40">
              <span>Curtir</span>
              <span>Comentar</span>
              <span>Compartilhar</span>
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <div
      className={`my-3 border-y border-on-surface/10 bg-surface px-3 py-3 text-on-surface ${className}`.trim()}
      data-post-no-open
    >
      <div className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-on-surface/45">
        {label}
      </div>
      <ins
        ref={adElementRef}
        className="adsbygoogle"
        style={{
          display: 'block',
          minHeight: 120,
          ...style,
        }}
        data-ad-client={normalizedPublisherId}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-ad-layout-key={layoutKey || undefined}
        data-full-width-responsive="true"
      />
    </div>
  );
};

AdSenseUnit.propTypes = {
  publisherId: PropTypes.string,
  slotId: PropTypes.string,
  format: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  label: PropTypes.string,
  variant: PropTypes.oneOf(['strip', 'feed-card']),
  layoutKey: PropTypes.string,
};

export default AdSenseUnit;
